import { useCallback, useRef } from 'react'

/**
 * 串行写入队列 Hook
 * 用于避免并发数据库写入导致的事务冲突
 */
export function useWriteQueue() {
  // 全局写入队列（确保串行执行）
  const writeQueueRef = useRef<Promise<void>>(Promise.resolve())

  /**
   * 将写入操作加入队列，确保串行执行
   * @param writeOperation 写入操作函数
   * @param delay 写入后的延迟时间（避免请求过快）
   */
  const queueWrite = useCallback(
    async <T>(
      writeOperation: () => Promise<T>,
      delay: number = 150
    ): Promise<T> => {
      let result: T

      // 将写入操作加入队列
      const writeTask = writeQueueRef.current.then(async () => {
        try {
          result = await writeOperation()
        } catch (err) {
          console.error('[写入队列] 操作失败:', err)
          throw err
        }
        // 每次写入后短暂延迟，避免请求过快
        if (delay > 0) {
          await new Promise<void>(r => setTimeout(r, delay))
        }
      })

      // 更新队列引用（防止队列中断）
      writeQueueRef.current = writeTask.catch(() => {})

      // 等待当前操作完成
      await writeTask

      return result!
    },
    []
  )

  /**
   * 等待队列中所有操作完成
   */
  const flushQueue = useCallback(async () => {
    await writeQueueRef.current
  }, [])

  return {
    queueWrite,
    flushQueue,
  }
}

